<template>
  <div class="inspiration-page white-theme">
    <div class="container">
      <div class="page-header">
        <span class="page-tag">{{ $t('gallery.tag') }}</span>
        <h1 class="page-title">{{ $t('inspiration.title') }}</h1>
        <p class="page-subtitle">{{ $t('inspiration.subtitle') }}</p>
      </div>

      <div class="inspiration-grid">
        <div 
          v-for="(item, idx) in items" 
          :key="idx" 
          class="inspiration-item"
          @click="openPreview(item)"
        >
          <div class="item-media">
            <img v-if="item.type === 'image'" :src="item.url" :alt="item.prompt" :width="item.width" :height="item.height" loading="lazy">
            <video v-else :src="item.url" muted loop @mouseenter="$el.play()" @mouseleave="$el.pause()"></video>
            <div class="item-overlay">
              <span class="item-model">{{ item.model }}</span>
            </div>
          </div>
          <div class="item-info">
            <p class="item-prompt">{{ truncate(item.prompt, 80) }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="preview-overlay" v-if="previewItem" @click.self="closePreview">
      <div class="preview-modal">
        <button class="modal-close" @click="closePreview">&times;</button>
        <div class="preview-content">
          <img v-if="previewItem.type === 'image'" :src="previewItem.url" :alt="previewItem.prompt" :width="previewItem.width" :height="previewItem.height">
          <video v-else :src="previewItem.url" controls autoplay loop></video>
        </div>
        <div class="preview-info">
          <div class="preview-header">
            <span class="preview-model">{{ previewItem.model }}</span>
          </div>
          <p class="preview-prompt">{{ previewItem.prompt }}</p>
          <div class="preview-actions">
            <button class="btn btn-secondary btn-sm" @click="usePrompt(previewItem)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              {{ $t('gallery.usePrompt') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import gallery1 from '@/assets/images/gallery-1.webp'
import gallery2 from '@/assets/images/gallery-2.webp'
import gallery3 from '@/assets/images/gallery-3.webp'
import gallery4 from '@/assets/images/gallery-4.webp'
import gallery5 from '@/assets/images/gallery-5.webp'
import gallery6 from '@/assets/images/gallery-6.webp'
import gallery7 from '@/assets/images/gallery-7.webp'
import gallery8 from '@/assets/images/gallery-8.webp'
import gptImageDemo1 from '@/assets/images/gpt-image-demo-1.webp'
import midjourneyDemo from '@/assets/images/midjourney-demo.webp'
import fluxKontext2 from '@/assets/images/flux-kontext-2.webp'
import fluxKontext3 from '@/assets/images/flux-kontext-3.webp'
import styleAfter from '@/assets/images/style-after.webp'
import styleTransfer from '@/assets/images/style-transfer.webp'
import template1 from '@/assets/images/template-1.webp'
import template2 from '@/assets/images/template-2.webp'
import template3 from '@/assets/images/template-3.webp'
import template4 from '@/assets/images/template-4.webp'
import hdUpscale from '@/assets/images/hd-upscale.webp'

import veo31Demo from '@/assets/images/veo31-demo.mp4'
import kling26Demo from '@/assets/images/kling26-demo.mp4'
import sora2Demo1 from '@/assets/images/sora2-demo-1.mp4'
import seedanceDemo from '@/assets/images/seedance-demo.mp4'
import hailuo23Demo2 from '@/assets/images/hailuo23-ai-video-demo2.mp4'
import wan25Demo from '@/assets/images/wan25-demo.mp4'
import runwayDemo from '@/assets/images/runway-demo.mp4'

export default {
  name: 'Inspiration',
  data() {
    return {
      previewItem: null,
      items: [
        {
          type: 'image',
          url: midjourneyDemo,
          prompt: 'A majestic castle floating in the clouds, ethereal lighting, fantasy art style, highly detailed, cinematic composition',
          model: 'Midjourney'
        },
        {
          type: 'image',
          url: gptImageDemo1,
          prompt: 'Professional product photography, elegant composition, soft studio lighting, high-end commercial style',
          model: 'GPT-Image-1'
        },
        {
          type: 'image',
          url: fluxKontext2,
          prompt: 'Creative digital art, vibrant colors, modern aesthetic, artistic interpretation',
          model: 'FLUX Kontext'
        },
        {
          type: 'image',
          url: fluxKontext3,
          prompt: 'Abstract visual composition, dynamic forms, contemporary art style, bold color palette',
          model: 'FLUX Kontext'
        },
        {
          type: 'image',
          url: gallery1,
          prompt: 'Stunning AI-generated artwork showcasing creative possibilities',
          model: 'Seedream 4'
        },
        {
          type: 'image',
          url: gallery2,
          prompt: 'Beautiful artistic creation with attention to detail and composition',
          model: 'FLUX Pro'
        },
        {
          type: 'image',
          url: gallery3,
          prompt: 'Creative visual expression through advanced AI image generation',
          model: 'Nano Banana'
        },
        {
          type: 'image',
          url: gallery4,
          prompt: 'Artistic masterpiece created with cutting-edge AI technology',
          model: 'Midjourney'
        },
        {
          type: 'image',
          url: gallery5,
          prompt: 'Imaginative scene rendered with photorealistic quality',
          model: 'GPT-Image-1'
        },
        {
          type: 'image',
          url: gallery6,
          prompt: 'Dreamy landscape with soft lighting and atmospheric depth',
          model: 'Seedream 4'
        },
        {
          type: 'image',
          url: gallery7,
          prompt: 'Dynamic composition with vibrant colors and artistic flair',
          model: 'FLUX Schnell'
        },
        {
          type: 'image',
          url: gallery8,
          prompt: 'Elegant visual design showcasing the power of AI creativity',
          model: 'FLUX Pro'
        },
        {
          type: 'image',
          url: styleAfter,
          prompt: 'Artistic style transfer result, transformed with creative visual effects',
          model: 'Style Transfer'
        },
        {
          type: 'image',
          url: styleTransfer,
          width: 960,
          height: 960,
          prompt: 'Creative style transformation applied to original image',
          model: 'Style Transfer'
        },
        {
          type: 'image',
          url: template1,
          prompt: 'Professional template design for creative projects',
          model: 'Seedream 4'
        },
        {
          type: 'image',
          url: template2,
          prompt: 'Modern design template with artistic elements',
          model: 'FLUX Pro'
        },
        {
          type: 'image',
          url: template3,
          prompt: 'Creative template showcasing design possibilities',
          model: 'Nano Banana'
        },
        {
          type: 'image',
          url: template4,
          prompt: 'Professional design template for various applications',
          model: 'GPT-Image-1'
        },
        {
          type: 'image',
          url: hdUpscale,
          width: 960,
          height: 640,
          prompt: 'High definition image upscale, enhanced resolution and clarity',
          model: 'HD Upscale'
        },
        {
          type: 'video',
          url: veo31Demo,
          prompt: 'Cinematic video generation with stunning visual effects and smooth motion',
          model: 'Veo 3.0'
        },
        {
          type: 'video',
          url: kling26Demo,
          prompt: 'High-quality video creation with realistic motion and detailed scenes',
          model: 'Kling 2.6'
        },
        {
          type: 'video',
          url: sora2Demo1,
          prompt: 'Advanced AI video generation with cinematic quality and creative storytelling',
          model: 'Sora 2'
        },
        {
          type: 'video',
          url: seedanceDemo,
          prompt: 'Dynamic video content with artistic direction and smooth transitions',
          model: 'Seedance'
        },
        {
          type: 'video',
          url: hailuo23Demo2,
          prompt: 'Creative video generation with unique visual style and engaging content',
          model: 'Hailuo 2.3'
        },
        {
          type: 'video',
          url: wan25Demo,
          prompt: 'Professional video output with high production value and creative vision',
          model: 'Wan 2.5'
        },
        {
          type: 'video',
          url: runwayDemo,
          prompt: 'Artistic video creation with innovative techniques and visual appeal',
          model: 'Runway'
        }
      ]
    }
  },
  methods: {
    truncate(str, len) {
      if (!str) return ''
      return str.length > len ? str.substring(0, len) + '...' : str
    },
    openPreview(item) {
      this.previewItem = item
    },
    closePreview() {
      this.previewItem = null
    },
    usePrompt(item) {
      var routeMap = {
        'Midjourney': '/models/midjourney',
        'Seedream 4': '/models/seedream-4',
        'FLUX Pro': '/models/flux-pro-1-1-ultra',
        'FLUX Schnell': '/models/flux-schnell',
        'FLUX Kontext': '/models/flux-kontext-2',
        // 'Nano Banana': '/models/nano-banana', // 暂时隐藏
        'GPT-Image-1': '/models/gpt-image-1',
        'Veo 3.0': '/models/veo-3-0',
        'Kling 2.6': '/models/kling-2-6',
        'Sora 2': '/models/sora-2',
        'Seedance': '/models/seedance',
        'Hailuo 2.3': '/models/hailuo-2-3',
        'Wan 2.5': '/models/wan-2-5',
        'Runway': '/models/runway'
      }
      var path = routeMap[item.model] || '/models'
      this.$router.push({ path: path, query: { prompt: item.prompt } })
    }
  }
}
</script>

<style scoped>
.inspiration-page {
  padding: 48px 0 80px;
  min-height: calc(100vh - var(--nav-height));
}

.page-header {
  text-align: center;
  margin-bottom: 40px;
}

.page-tag {
  display: inline-block;
  padding: 6px 16px;
  background: var(--accent-light);
  color: var(--accent);
  border-radius: var(--radius-full);
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 16px;
}

.page-title {
  font-size: 36px;
  font-weight: 800;
  margin-bottom: 12px;
}

.page-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  max-width: 600px;
  margin: 0 auto;
}

.inspiration-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.inspiration-item {
  background: var(--bg-card);
  border: 1px solid var(--bg-card-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s;
}

.inspiration-item:hover {
  border-color: var(--accent);
  transform: translateY(-4px);
}

.item-media {
  position: relative;
  aspect-ratio: 4/3;
  overflow: hidden;
  background: linear-gradient(135deg, #e8e8f0, #d0d0e8);
}

.item-media img,
.item-media video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 40px 12px 12px;
  background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
  opacity: 0;
  transition: opacity 0.2s;
}

.inspiration-item:hover .item-overlay {
  opacity: 1;
}

.item-model {
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  background: rgba(0,0,0,0.4);
  padding: 4px 8px;
  border-radius: var(--radius-sm);
}

.item-info {
  padding: 12px;
}

.item-prompt {
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.5;
  margin: 0;
}

.preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

.preview-modal {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-xl);
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
}

.modal-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: var(--text-muted);
  background: rgba(0,0,0,0.5);
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  z-index: 10;
}

.modal-close:hover {
  color: var(--text-primary);
  background: rgba(0,0,0,0.7);
}

.preview-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  min-height: 300px;
  max-height: 60vh;
}

.preview-content img,
.preview-content video {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.preview-info {
  padding: 20px;
}

.preview-header {
  margin-bottom: 12px;
}

.preview-model {
  display: inline-block;
  padding: 4px 12px;
  background: var(--accent-light);
  color: var(--accent);
  border-radius: var(--radius-sm);
  font-size: 12px;
  font-weight: 600;
}

.preview-prompt {
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 16px;
}

.preview-actions {
  display: flex;
  gap: 12px;
}

@media (max-width: 768px) {
  .inspiration-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .page-title {
    font-size: 28px;
  }
}
@media (max-width: 480px) {
  .inspiration-grid {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
  .page-title {
    font-size: 24px;
  }
}
</style>
